import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import { Zap, Trophy, ClipboardCheck, Sparkles, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

export default function SharePage() {
  const { shareId } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealAnswers, setRevealAnswers] = useState(false);

  useEffect(() => {
    api.get(`/share/${shareId}`)
      .then(res => setShareData(res.data))
      .catch(() => setError('Dinámica no encontrada o enlace inválido'))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="w-9 h-9 border-2 border-slate-700 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030712] flex flex-col items-center justify-center p-6 gap-4">
        <p className="text-slate-400 text-lg">{error}</p>
        <Link to="/auth" className="text-blue-400 hover:text-blue-300 underline text-sm">
          Ir a DinamiClass
        </Link>
      </div>
    );
  }

  const cards = shareData.cards || {};
  // LÓGICA DE COMPATIBILIDAD Y MULTI-SESIÓN
  const clasesArray = cards.clases ? cards.clases : [cards];

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* ── Conversion Banner ── */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-950/90 via-violet-950/70 to-blue-950/90 border-b border-blue-500/25 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/3d891077-d4d4-4c86-bf60-f7dc6784f6ce/images/dd0a2aea755b0d46abf8e27d6ec050f1d03741501a1fd94a00d0af2e9feb4cb9.png"
              alt="DinamiClass"
              className="h-7 w-7 rounded-lg flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-white text-sm font-bold leading-tight">Creado con DinamiClass</p>
              <p className="text-blue-300/70 text-xs hidden sm:block truncate">
                Transforma cualquier tema en dinámicas de clase en 30 segundos con IA
              </p>
            </div>
          </div>
          <Link
            data-testid="share-page-cta"
            to="/auth"
            className="flex-shrink-0 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded-full transition-all shadow-lg shadow-blue-600/30 whitespace-nowrap"
          >
            <Sparkles size={13} strokeWidth={1.5} />
            <span>Crear cuenta gratis</span>
            <ArrowRight size={13} strokeWidth={2} />
          </Link>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <div>
          <p className="text-xs text-blue-400 uppercase tracking-[0.15em] font-semibold mb-1">
            Compartido por {shareData.creator_name || 'un colega'}
          </p>
          <h1 className="text-2xl font-bold text-white">{shareData.topic} · {shareData.age} años</h1>
        </div>

        {/* ITERAMOS SOBRE LAS CLASES (Igual que en el Dashboard) */}
        <div className="space-y-12">
          {clasesArray.map((clase, index) => {
            const apertura = clase.desafio_apertura || clase.gancho;
            const reto = clase.reto;
            const evaluacion = clase.evaluacion;

            return (
              <div key={index} className="space-y-4">
                
                {/* Separador de Sesión */}
                {(clasesArray.length > 1 || clase.titulo_clase) && (
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-800"></div>
                    <h3 className="text-slate-400 font-bold text-sm tracking-wider uppercase">
                      {clase.titulo_clase || `Sesión ${index + 1}`} {clase.duracion_minutos ? `(${clase.duracion_minutos} min)` : ''}
                    </h3>
                    <div className="h-px flex-1 bg-slate-800"></div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* DESAFÍO DE APERTURA */}
                  {apertura && (
                    <div className="bg-slate-900 border border-blue-500/30 rounded-xl p-5 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-24 h-24 bg-blue-600/8 rounded-full -translate-x-6 -translate-y-6 pointer-events-none" />
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Zap size={14} className="text-blue-400" strokeWidth={1.5} />
                          </div>
                          <span className="text-xs text-blue-400/70 bg-blue-500/10 px-2 py-0.5 rounded-full">
                            {apertura.tiempo || '2 min'} · Apertura
                          </span>
                        </div>
                        <h3 className="text-blue-400 font-bold text-sm mb-2">Desafío de Apertura</h3>
                        <p className="text-white text-sm font-semibold mb-2 leading-snug">{apertura.titulo}</p>
                        <p className="text-slate-300 text-xs leading-relaxed mb-3">{apertura.pregunta_polemica}</p>
                        {apertura.instrucciones && (
                          <div className="border-t border-slate-800 pt-3">
                            <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Para el docente</p>
                            <p className="text-slate-400 text-xs leading-relaxed">{apertura.instrucciones}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* RETO */}
                  {reto && (
                    <div className="bg-slate-900 border border-violet-500/30 rounded-xl p-5 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-24 h-24 bg-violet-600/8 rounded-full -translate-x-6 -translate-y-6 pointer-events-none" />
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center">
                            <Trophy size={14} className="text-violet-400" strokeWidth={1.5} />
                          </div>
                          <span className="text-xs text-violet-400/70 bg-violet-500/10 px-2 py-0.5 rounded-full">
                            {reto.tiempo || '10-15 min'} · Grupal
                          </span>
                        </div>
                        <h3 className="text-violet-400 font-bold text-sm mb-2">El Reto Gamificado</h3>
                        <p className="text-white text-sm font-semibold mb-2 leading-snug">{reto.titulo}</p>
                        <p className="text-slate-300 text-xs leading-relaxed mb-3">{reto.descripcion}</p>
                        {reto.instrucciones && (
                          <div className="border-t border-slate-800 pt-3">
                            <p className="text-slate-600 text-xs uppercase tracking-wider mb-1">Paso a paso</p>
                            <p className="text-slate-400 text-xs leading-relaxed whitespace-pre-line">{reto.instrucciones}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* EVALUACIÓN */}
                  {evaluacion && (
                    <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-5 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-24 h-24 bg-emerald-600/8 rounded-full -translate-x-6 -translate-y-6 pointer-events-none" />
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                            <ClipboardCheck size={14} className="text-emerald-400" strokeWidth={1.5} />
                          </div>
                          <span className="text-xs text-emerald-400/70 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                            5 min · Cierre
                          </span>
                        </div>
                        <h3 className="text-emerald-400 font-bold text-sm mb-2">Micro-Evaluación</h3>
                        <p className="text-white text-sm font-semibold mb-3 leading-snug">{evaluacion.titulo}</p>
                        <div className="space-y-2.5">
                          {evaluacion.preguntas?.map((q, i) => (
                            <div key={i} className="bg-slate-800/60 rounded-lg p-2.5">
                              <p className="text-slate-200 text-xs font-medium mb-1.5">{i + 1}. {q.pregunta}</p>
                              <div className="space-y-1">
                                {q.opciones?.map((opt, j) => (
                                  <div
                                    key={j}
                                    className={`text-xs px-2 py-1 rounded transition-colors ${
                                      revealAnswers && opt.startsWith(q.respuesta_correcta)
                                        ? 'bg-emerald-500/20 text-emerald-300'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {opt}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => setRevealAnswers(!revealAnswers)}
                          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-emerald-400 border border-slate-700 hover:border-emerald-500/40 rounded-lg py-2 transition-all"
                        >
                          {revealAnswers ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          {revealAnswers ? 'Ocultar respuestas' : 'Revelar respuestas'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="bg-gradient-to-r from-blue-900/25 via-violet-900/15 to-blue-900/25 border border-blue-500/20 rounded-2xl p-7 text-center mt-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.15em] font-semibold mb-2">
            ¿Te gustaron estas dinámicas?
          </p>
          <h2 className="text-2xl font-bold text-white mb-2">Generá las tuyas en 30 segundos</h2>
          <p className="text-slate-400 text-sm mb-5 max-w-md mx-auto">
            Registrate gratis y obtenés 3 dinámicas para empezar a transformar tus clases hoy mismo.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-7 py-3.5 rounded-xl transition-all shadow-lg shadow-blue-600/20 text-sm"
          >
            <Sparkles size={16} strokeWidth={1.5} />
            Crear cuenta gratis — es gratis
            <ArrowRight size={15} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </div>
  );
}
